import React, { type FC, type PropsWithChildren, type ReactElement } from 'react'
import { t } from 'i18next'
import { useIsMobileVariant, Box, Button, type ButtonProps } from '@dhl-ui/components'
import { Page } from '@dhl-ui/page'
import { FooterButtonsContainer, ScrollablePageContent } from '~/components/index.js'

type ChildWithMaybeDisplayName = ReactElement & { type?: { displayName?: string } }

type FormButtonProps = Omit<ButtonProps, 'fullWidth'>

const FormButton: FC<FormButtonProps> = ({ children, ...rest }) => {
  const isMobileVariant = useIsMobileVariant()

  return (
    <Button {...rest} fullWidth={isMobileVariant}>
      {children}
    </Button>
  )
}

type SaveButtonProps = Omit<ButtonProps, 'fullWidth' | 'type' | 'variant'>

const SaveButton: FC<SaveButtonProps> = ({ children, ...rest }) => {
  const isMobileVariant = useIsMobileVariant()

  return (
    <Button
      data-testid="form-subpage-save-button"
      {...rest}
      fullWidth={isMobileVariant}
      type="submit"
      variant="contained"
    >
      {children ?? t('common.saveButton')}
    </Button>
  )
}

SaveButton.displayName = 'SaveButton'

type CancelButtonProps = Omit<ButtonProps, 'fullWidth' | 'type' | 'variant'>

const CancelButtonDesktop: FC<CancelButtonProps> = ({ children, ...rest }) => {
  const isMobileVariant = useIsMobileVariant()

  if (isMobileVariant) {
    return null
  }

  return (
    <Button
      data-testid="form-subpage-cancel-button"
      {...rest}
      color="alternative"
      fullWidth={isMobileVariant}
      variant="text"
    >
      {children ?? t('common.cancelButton')}
    </Button>
  )
}

CancelButtonDesktop.displayName = 'CancelButtonDesktop'

const Buttons = ({ children }: PropsWithChildren) => {
  const isMobileVariant = useIsMobileVariant()
  let cancelButton: ReactElement | null = null
  let saveButton: ReactElement | null = null
  const otherButtons: ReactElement[] = []

  React.Children.forEach(children, (child: ChildWithMaybeDisplayName) => {
    if (!React.isValidElement(child)) {
      return
    }

    const childType = child?.type?.displayName

    if (childType === CancelButtonDesktop.displayName) {
      cancelButton = child
    } else if (childType === SaveButton.displayName) {
      saveButton = child
    } else {
      otherButtons.push(child)
    }
  })

  if (isMobileVariant) {
    return (
      <>
        {saveButton}
        {otherButtons}
      </>
    )
  }

  return (
    <Box sx={{ display: 'flex', mt: 2, justifyContent: 'flex-end', gap: 2 }}>
      {cancelButton}
      {otherButtons}
      {saveButton}
    </Box>
  )
}

Buttons.displayName = 'Buttons'

const FormContentContainer: FC<PropsWithChildren> = ({ children }) => {
  const isMobileVariant = useIsMobileVariant()
  let buttonsContainer: ReactElement | null = null
  const otherChildren: ReactElement[] = []

  React.Children.forEach(children, (child: ChildWithMaybeDisplayName) => {
    if (!React.isValidElement(child)) {
      return
    }

    if (child?.type?.displayName === Buttons.displayName) {
      buttonsContainer = child
    } else {
      otherChildren.push(child)
    }
  })

  if (otherChildren.length === 0) {
    return null
  }

  if (!isMobileVariant) {
    return (
      <>
        {otherChildren}
        {buttonsContainer}
      </>
    )
  }

  return (
    <Page.Content isFullView>
      <ScrollablePageContent
        sx={{
          paddingBottom: (theme) => theme.spacing(10),
        }}
      >
        {otherChildren}
      </ScrollablePageContent>

      {buttonsContainer && <FooterButtonsContainer>{buttonsContainer}</FooterButtonsContainer>}
    </Page.Content>
  )
}

type FormContentProps = FC<PropsWithChildren> & {
  Buttons: FC<PropsWithChildren>
  Button: FC<FormButtonProps>
  SaveButton: FC<SaveButtonProps>
  CancelButtonDesktop: FC<CancelButtonProps>
}

const FormContent: FormContentProps = Object.assign(FormContentContainer, {
  Buttons,
  Button: FormButton,
  SaveButton,
  CancelButtonDesktop,
})

export { FormContent }
